import React from "react";

/* eslint-disable react/prop-types */

// Default "Button Style" values: at these the stage-relative assessment tokens
// (--q-btn-pad-x: 1.4em, --q-font: 1em) are reproduced exactly, so an
// unconfigured button keeps its current size.
const DEFAULT_PADDING = 8;
const DEFAULT_FONT_SIZE = 14;

// Size settings for an assessment .tryButton. sass/components/_assessment.scss
// pins padding / font-size with !important (stage-relative em sizing), so the
// author's values are handed over as custom properties it reads instead.
// Values are em so they keep scaling with the stage in editor, visor and export.
export function getQuestionButtonSizeVars(settings = {}, fontSizeOverride) {
  const padding = Number(settings.buttonPadding ?? DEFAULT_PADDING);
  const fontSize = Number(fontSizeOverride ?? settings.buttonFontSize ?? DEFAULT_FONT_SIZE);
  return {
    "--q-btn-custom-pad-x": `${(padding / DEFAULT_PADDING) * 1.4}em`,
    "--q-btn-custom-font": `${fontSize / DEFAULT_FONT_SIZE}em`,
  };
}

// `settings` carries the plugin's "Button Style" state (buttonBackgroundColor,
// buttonBorderColor, buttonHoverBackgroundColor, buttonBorderWidth/Radius, ...)
// so every one of those settings actually drives this button. Fallbacks match the
// button's original hardcoded design, so an unconfigured button looks unchanged.
export default function AnswerButton({
  onClick,
  disabled,
  hidden,
  text,
  fontSize,
  fontWeight,
  settings = {},
}) {
  const resolvedBackground = settings.buttonBackgroundColor || "#FAFAFA";
  const resolvedHoverBackground = settings.buttonHoverBackgroundColor || "#F0F0F0";
  // Legacy dead-code default persisted white text on the default light button;
  // coerce only that combination to readable black so pre-existing instances
  // don't go invisible. White text an author picks for a dark button is kept.
  const rawColor = settings.buttonTextColor || "#000000";
  const isLegacyWhiteOnDefault =
    rawColor.toLowerCase() === "#ffffff" && resolvedBackground.toLowerCase() === "#fafafa";
  const resolvedColor = isLegacyWhiteOnDefault ? "#000000" : rawColor;
  const resolvedBorderColor = settings.buttonBorderColor || "#E5E5E5";
  const resolvedBorderWidth = settings.buttonBorderWidth ?? 1;
  const resolvedBorderRadius = settings.buttonBorderRadius ?? 8;
  const resolvedFontSize = fontSize ?? settings.buttonFontSize ?? DEFAULT_FONT_SIZE;
  const resolvedFontWeight = fontWeight || settings.buttonFontWeight || "normal";
  const resolvedPadding = settings.buttonPadding ?? DEFAULT_PADDING;
  const baseStyle = {
    backgroundColor: resolvedBackground,
    color: resolvedColor,
    borderRadius: `${resolvedBorderRadius}px`,
    height: "48px",
    padding: `0 ${resolvedPadding * 3}px`,
    border: `${resolvedBorderWidth}px solid ${resolvedBorderColor}`,
    cursor: disabled ? "not-allowed" : "pointer",
    margin: "2px",
    fontSize: `${resolvedFontSize}px`,
    fontWeight: resolvedFontWeight,
    display: hidden ? "none" : "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s ease",
    opacity: disabled ? 0.5 : 1,
    ...getQuestionButtonSizeVars(settings, fontSize),
  };

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      onClick={handleClick}
      style={baseStyle}
      className={"tryButton"}
      disabled={disabled}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = resolvedHoverBackground;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = resolvedBackground;
      }}
    >
      {text}
    </button>
  );
}

/* eslint-enable react/prop-types */
